import React from 'react';

import {
  IconActivityHeartbeat,
  IconCalendarMonth,
  IconChartAreaLine,
  IconClipboardCheck,
  IconFileDollar,
  IconFileSearch,
  IconHelpSquare,
  IconHierarchy,
  IconListSearch,
  IconPrinter,
  IconReceipt,
} from '@tabler/icons-react';
import _ from 'lodash';

import {
  ADMIN_INVOICES_HREF,
  ADMIN_PAYROLL_HREF,
  ADMIN_PRINTING_HREF,
  ADMIN_QUIZ_SUBMISSIONS_HREF,
  ADMIN_TIMESHEET_HREF,
  ANALYTICS_HREF,
  APPS_HREF,
  NILE_QUESTIONS_HREF,
  NILE_SOURCES_HREF,
  NILE_TAXONOMY_HREF,
  QUIZ_HREF,
  STUDENT_ACTIVITY_LOG_HREF,
} from '../../utils/common/hrefs';
import { hasOverlap } from './utils';

import { UserGroup } from '@repo/validation';

// The gallery's sections, in the order it shows them.
export const APP_SECTIONS = ['Admin', 'Analytics', 'Nile', 'Beta features'] as const;

export type AppSection = (typeof APP_SECTIONS)[number];

// One entry in the Apps gallery: a page reached often enough to need a home,
// but not often enough to hold a place in the sidebar.
export interface AppOption {
  icon: React.ReactElement;
  name: string;
  href: string;
  // Endpoints the app owns, so the sidebar's Apps entry stays selected while
  // the reader is inside one.
  linkedEndpoints: string[];
  groups: UserGroup[];
  section: AppSection;
}

export interface AppSectionGroup {
  section: AppSection;
  apps: AppOption[];
}

// Who may open the Quiz app: its tile and every one of its routes, matching
// the question set endpoints' own gate.
export const QUIZ_GROUPS: UserGroup[] = [UserGroup.Owners];

// Who may open Quiz submissions: its tile and its route, matching the attempt
// scan and submission endpoints' own gate.
export const QUIZ_SUBMISSION_GROUPS: UserGroup[] = [UserGroup.Owners];

const APPS: AppOption[] = [
  {
    icon: <IconReceipt />,
    name: 'Invoices',
    href: ADMIN_INVOICES_HREF,
    linkedEndpoints: ['admin/invoices'],
    groups: [UserGroup.Admins],
    section: 'Admin',
  },
  {
    icon: <IconFileDollar />,
    name: 'Payroll',
    href: ADMIN_PAYROLL_HREF,
    linkedEndpoints: ['admin/payroll'],
    groups: [UserGroup.Managers],
    section: 'Admin',
  },
  {
    icon: <IconPrinter />,
    name: 'Printing',
    href: ADMIN_PRINTING_HREF,
    linkedEndpoints: ['admin/printing'],
    groups: [UserGroup.Admins],
    section: 'Admin',
  },
  {
    icon: <IconCalendarMonth />,
    name: 'Timesheet',
    href: ADMIN_TIMESHEET_HREF,
    linkedEndpoints: ['admin/timesheet'],
    groups: [UserGroup.Managers],
    section: 'Admin',
  },
  {
    icon: <IconActivityHeartbeat />,
    name: 'Activity log',
    href: STUDENT_ACTIVITY_LOG_HREF,
    linkedEndpoints: ['student-activity-log'],
    groups: [UserGroup.Admins, UserGroup.Teachers],
    section: 'Analytics',
  },
  {
    icon: <IconChartAreaLine />,
    name: 'Student analytics',
    href: ANALYTICS_HREF,
    linkedEndpoints: ['analytics'],
    groups: [UserGroup.Admins, UserGroup.Teachers],
    section: 'Analytics',
  },
  {
    icon: <IconListSearch />,
    name: 'Nile questions',
    href: NILE_QUESTIONS_HREF,
    linkedEndpoints: ['nile-questions'],
    groups: [UserGroup.Owners],
    section: 'Nile',
  },
  {
    icon: <IconFileSearch />,
    name: 'Nile sources',
    href: NILE_SOURCES_HREF,
    linkedEndpoints: ['nile-sources'],
    groups: [UserGroup.Owners],
    section: 'Nile',
  },
  {
    icon: <IconHierarchy />,
    name: 'Nile taxonomy',
    href: NILE_TAXONOMY_HREF,
    linkedEndpoints: ['nile-taxonomy'],
    groups: [UserGroup.Owners],
    section: 'Nile',
  },
  {
    icon: <IconHelpSquare />,
    name: 'Quiz',
    href: QUIZ_HREF,
    linkedEndpoints: ['quiz'],
    groups: QUIZ_GROUPS,
    section: 'Beta features',
  },
  {
    icon: <IconClipboardCheck />,
    name: 'Quiz submissions',
    href: ADMIN_QUIZ_SUBMISSIONS_HREF,
    linkedEndpoints: ['admin/quiz-submissions'],
    groups: QUIZ_SUBMISSION_GROUPS,
    section: 'Beta features',
  },
];

// Every group with an app behind it, which the gallery's route is gated on.
export const APP_GROUPS: UserGroup[] = _.uniq(APPS.flatMap(app => app.groups));

// The apps a caller may open, in gallery order: by section, then by name.
export const getAppOptions = (userGroups: string[]): AppOption[] =>
  _.sortBy(
    APPS.filter(app => hasOverlap(userGroups, app.groups)),
    app => APP_SECTIONS.indexOf(app.section),
    app => app.name.toLowerCase()
  );

// Groups apps under their sections in gallery order, leaving out any section
// with none of them.
export const groupAppsBySection = (apps: AppOption[]): AppSectionGroup[] =>
  APP_SECTIONS.map(section => ({
    section,
    apps: apps.filter(app => app.section === section),
  })).filter(group => group.apps.length > 0);

// Every endpoint an app owns, so the sidebar's Apps entry highlights on all of
// them rather than only on the gallery itself.
export const getAppEndpoints = (): string[] => [
  APPS_HREF.slice(1),
  ...APPS.flatMap(app => app.linkedEndpoints),
];
